import { doc, onSnapshot } from 'firebase/firestore'
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '../services/api'
import { db } from '../services/firebase'
import { useUserStore } from './userStore'

export const useContinuityStore = defineStore('continuity', () => {
  const userStore = useUserStore()
  const router = useRouter()
  const route = useRoute()
  
  const remoteContext = ref(null)
  const showResumePrompt = ref(false)
  const lastUpdateByMe = ref(false)
  const deviceId = ref(localStorage.getItem('apolaki_device_id') || `device_${Math.random().toString(36).slice(2, 11)}`)
  
  // Store device ID for cross-device detection
  localStorage.setItem('apolaki_device_id', deviceId.value)

  let unsubscribe = null

  // Watch for Auth changes to start/stop listening
  watch(() => userStore.isAuthenticated, (isAuth) => {
    if (isAuth) {
      startSync()
    } else {
      stopSync()
    }
  }, { immediate: true })

  // Watch for Route changes to update remote context (Auto-sync)
  watch(() => route.path, (newPath) => {
    if (userStore.isAuthenticated && !['/login', '/signup', '/'].includes(newPath)) {
      updateRemoteContext(newPath)
    }
  })

  function startSync() {
    if (unsubscribe) return
    
    // Only sync with Firestore if we have a valid Firebase user
    // Local-only sessions (Postgres) do not have Firestore access tokens
    if (!userStore.firebaseUser || !userStore.user?.id) {
      console.log('[Continuity] Skipping Firestore sync for local/unauthenticated session')
      return
    }

    const contextRef = doc(db, 'users', userStore.user.id, 'private', 'context')
    
    unsubscribe = onSnapshot(contextRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data()
        remoteContext.value = data
        
        // Only show prompt if:
        // 1. Action was on a DIFFERENT device
        // 2. We aren't already on that view
        // 3. It happened recently (last 30 mins)
        const isDifferentDevice = data.deviceId !== deviceId.value
        const isNewView = data.view !== route.path
        const isRecent = data.updatedAt?.toMillis() > Date.now() - 30 * 60 * 1000

        if (isDifferentDevice && isNewView && isRecent) {
          showResumePrompt.value = true
        } else {
          showResumePrompt.value = false
        }
      }
    })
  }

  function stopSync() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
  }

  async function updateRemoteContext(view, data = {}) {
    try {
      lastUpdateByMe.value = true
      await api.post('/personas/context', {
        view,
        data,
        deviceId: deviceId.value
      })
    } catch (err) {
      console.warn('[Continuity] Failed to sync context:', err.message)
    }
  }

  async function resumeWork() {
    if (remoteContext.value?.view) {
      showResumePrompt.value = false
      await router.push(remoteContext.value.view)
    }
  }

  return {
    remoteContext,
    showResumePrompt,
    deviceId,
    resumeWork,
    updateRemoteContext
  }
})
